import * as THREE from 'three';

const BEACON_HEIGHT = 5.4;
const BEACON_BASE_RADIUS = 0.42;

/**
 * Tiny surface objective beacon used by the tutorial when a step asks
 * the player to walk somewhere. A holographic post + pulsing ring with
 * a soft point light — distinctive enough to spot at a distance, cheap
 * enough to spawn/dispose on demand.
 *
 * The beacon owns its own meshes/materials; `dispose()` releases them.
 * It is purely visual — distance checks live in the GameDirector so
 * the same code can update the surface minimap mission target.
 */
export class MissionSurfaceBeacon {
  readonly object3D: THREE.Group;
  readonly position: THREE.Vector3;

  private readonly post: THREE.Mesh;
  private readonly postMat: THREE.MeshStandardMaterial;
  private readonly ring: THREE.Mesh;
  private readonly ringMat: THREE.MeshBasicMaterial;
  private readonly haloMat: THREE.MeshBasicMaterial;
  private readonly halo: THREE.Mesh;
  private readonly light: THREE.PointLight;
  private elapsed = 0;
  private disposed = false;

  constructor(position: THREE.Vector3) {
    this.position = position.clone();
    this.object3D = new THREE.Group();
    this.object3D.position.copy(this.position);

    // Vertical post — emissive red so the beacon reads against the
    // pale Caelis salt flats at distance.
    this.postMat = new THREE.MeshStandardMaterial({
      color: 0xff5d5d,
      emissive: 0xff8a8a,
      emissiveIntensity: 1.4,
      roughness: 0.4,
      metalness: 0.2
    });
    this.post = new THREE.Mesh(
      new THREE.CylinderGeometry(
        BEACON_BASE_RADIUS * 0.45,
        BEACON_BASE_RADIUS,
        BEACON_HEIGHT,
        16
      ),
      this.postMat
    );
    this.post.position.y = BEACON_HEIGHT / 2;
    this.object3D.add(this.post);

    // Pulsing ring at the top — calls the eye like a marker.
    this.ringMat = new THREE.MeshBasicMaterial({
      color: 0xff8a8a,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.ring = new THREE.Mesh(
      new THREE.RingGeometry(0.6, 0.85, 32),
      this.ringMat
    );
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = BEACON_HEIGHT + 0.1;
    this.object3D.add(this.ring);

    // Faint ground halo so the beacon reads even when the player is
    // looking down at the floor.
    this.haloMat = new THREE.MeshBasicMaterial({
      color: 0xff5d5d,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.halo = new THREE.Mesh(
      new THREE.RingGeometry(1.2, 2.6, 48),
      this.haloMat
    );
    this.halo.rotation.x = -Math.PI / 2;
    this.halo.position.y = 0.05;
    this.object3D.add(this.halo);

    // Subtle light — local-only, doesn't affect performance much.
    this.light = new THREE.PointLight(0xff8a8a, 0.9, 14, 2.0);
    this.light.position.y = BEACON_HEIGHT * 0.85;
    this.object3D.add(this.light);
  }

  update(dt: number): void {
    if (this.disposed) return;
    this.elapsed += dt;
    const pulse = 0.55 + 0.45 * Math.sin(this.elapsed * 3.4);
    this.ringMat.opacity = 0.55 + 0.4 * pulse;
    this.haloMat.opacity = 0.18 + 0.22 * pulse;
    this.light.intensity = 0.6 + 0.8 * pulse;
    this.ring.rotation.z = this.elapsed * 0.7;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.post.geometry.dispose();
    this.postMat.dispose();
    this.ring.geometry.dispose();
    this.ringMat.dispose();
    this.halo.geometry.dispose();
    this.haloMat.dispose();
    this.object3D.parent?.remove(this.object3D);
  }
}
